/**
 * The file information screen (§31).
 *
 * §31 ends with a rule that shapes this whole module: *"Display only
 * information that the system can verify. Do not show unsupported claims such
 * as '100% safe.'"*
 *
 * So every field here is derived from something the pipeline actually
 * established — the detection result, the security assessment, the handler's
 * report, the save outcome. Nothing is asserted because it is generally true of
 * the product; anything not known is `null`, and the UI renders that as
 * "not known" rather than as a reassuring default.
 */
import { canEdit, canView } from '@moduviewer/core';
import type {
  DetectionResult, DocumentSession, FormatSpec, PlatformTier,
  SecurityAssessment, SupportLevel,
} from '@moduviewer/core';
import type { OpenedDocument, ValidationReport } from '@moduviewer/handlers';

export type Tri = 'yes' | 'no' | 'unknown';

export interface FileInfoRow {
  /** i18n key for the label (§25). */
  readonly labelKey: string;
  /**
   * i18n key for the value, when the value is one of a fixed set.
   *
   * Explicitly `| undefined` rather than merely optional: rows are assembled
   * programmatically, and under `exactOptionalPropertyTypes` a computed
   * `undefined` is not the same as an absent key.
   */
  readonly valueKey?: string | undefined;
  /** Literal value — a filename, a size, a timestamp. Never translated. */
  readonly value?: string | undefined;
  /** Extra context shown on request. */
  readonly detail?: string | undefined;
}

export interface FileInfo {
  readonly rows: readonly FileInfoRow[];
  /** Blocked capabilities, for the security section. */
  readonly blocked: readonly { readonly kind: string; readonly detail: string }[];
  /** Warnings collected across detection and the handler. */
  readonly warnings: readonly { readonly messageKey: string; readonly detail?: string }[];
  /** Features this format cannot keep on save (§13, §24). */
  readonly lossOnSave: readonly string[];
  readonly sensitive: boolean;
}

export interface FileInfoInput {
  readonly detection: DetectionResult;
  readonly assessment: SecurityAssessment;
  readonly spec: FormatSpec | null;
  readonly document: OpenedDocument<unknown> | null;
  readonly session: DocumentSession | null;
  readonly tier: PlatformTier;
  /** Where the working copy lives, when one exists. */
  readonly workingCopyLocation?: string | null;
  /** When temporary artefacts are scheduled for deletion. */
  readonly cacheExpiresAtMs?: number | null;
  /** Result of reopening the exported file (§42-27). */
  readonly validation?: ValidationReport | null;
  /** True only if this file was actually uploaded somewhere (§29). */
  readonly transmittedExternally?: boolean;
}

const SOURCE_LABEL: Readonly<Record<string, string>> = {
  'external-app': 'fileInfo.accessSource',
  'file-picker': 'fileInfo.accessSource',
  recent: 'fileInfo.accessSource',
  'drag-drop': 'fileInfo.accessSource',
  'command-line': 'fileInfo.accessSource',
};

function supportKey(level: SupportLevel): string {
  return `support.${level}`;
}

function triKey(value: Tri): string {
  return value === 'yes' ? 'fileInfo.value.yes'
    : value === 'no' ? 'fileInfo.value.no'
      : 'fileInfo.value.unknown';
}

export function buildFileInfo(input: FileInfoInput): FileInfo {
  const { detection, assessment, spec, document, session } = input;
  const rows: FileInfoRow[] = [];

  const add = (labelKey: string, row: Omit<FileInfoRow, 'labelKey'>): void => {
    rows.push({ labelKey, ...row });
  };

  /* ---- identity ---- */

  add('fileInfo.fileName', { value: session?.original.displayName ?? '' });
  add('fileInfo.fileType', {
    value: spec?.label ?? detection.formatId ?? '',
    valueKey: spec ? undefined : 'fileInfo.value.unknown',
    detail: `confidence: ${detection.confidence}`,
  });
  add('fileInfo.detectedMime', {
    // What the *sender* claimed, shown verbatim — it is evidence about the
    // sender, and §31 asks for it specifically.
    value: detection.evidence.declaredMime ?? '',
    valueKey: detection.evidence.declaredMime ? undefined : 'fileInfo.value.unknown',
    detail: detection.evidence.bySignature
      ? `identified from content as ${detection.evidence.bySignature}`
      : 'not identified from content',
  });
  if (session) {
    add('fileInfo.fileSize', { value: String(session.original.sizeBytes) });
  }

  /* ---- state ---- */

  add('fileInfo.originalOrCopy', {
    valueKey: session?.lastSaved?.replacedOriginal === false && session.savedRevision > 0
      ? 'fileInfo.value.editedCopy'
      : 'fileInfo.value.original',
  });
  add('fileInfo.readOnly', {
    valueKey: triKey(document ? (document.readOnly ? 'yes' : 'no') : 'unknown'),
    detail: assessment.forceReadOnly
      ? 'the security assessment forces read-only for this file'
      : undefined,
  });

  /* ---- support levels ---- */

  if (spec) {
    const platformKey = input.tier === 'desktop' ? 'windows'
      : input.tier === 'web' ? 'web' : input.tier === 'mobile-low' ? 'android' : 'android';
    const [platformView, platformEdit] = spec.platforms[platformKey as keyof typeof spec.platforms];
    add('fileInfo.viewSupportLevel', {
      valueKey: supportKey(platformView),
      detail: canView(platformView) ? undefined : 'this platform cannot render this format',
    });
    add('fileInfo.editSupportLevel', {
      valueKey: supportKey(platformEdit),
      detail: canEdit(platformEdit) ? undefined : 'editing is not offered for this format here',
    });
  } else {
    add('fileInfo.viewSupportLevel', { valueKey: 'support.unsupported' });
    add('fileInfo.editSupportLevel', { valueKey: 'support.unsupported' });
  }

  /* ---- privacy and security ---- */

  add('fileInfo.processedLocally', {
    // True because nothing in the open pipeline has a network path — not
    // because we would like it to be true.
    valueKey: triKey(input.transmittedExternally ? 'no' : 'yes'),
  });
  add('fileInfo.transmittedExternally', {
    valueKey: triKey(input.transmittedExternally ? 'yes' : 'no'),
  });
  add('fileInfo.macrosBlocked', {
    valueKey: triKey(
      assessment.blockedFeatures.some((f) => f.kind === 'macros' || f.kind === 'scripts')
        ? 'yes' : 'no',
    ),
    detail: assessment.blockedFeatures.map((f) => f.kind).join(', ') || undefined,
  });
  add('fileInfo.passwordStatus', {
    valueKey: detection.warnings.some((w) => w.code === 'encrypted')
      ? 'detect.warning.encrypted'
      : 'fileInfo.value.no',
  });

  if (session) {
    add(SOURCE_LABEL[session.original.source] ?? 'fileInfo.accessSource', {
      value: session.original.source,
    });
  }

  /* ---- temporary storage ---- */

  add('fileInfo.tempLocation', {
    value: input.workingCopyLocation ?? '',
    valueKey: input.workingCopyLocation ? undefined : 'fileInfo.value.never',
  });
  add('fileInfo.cacheDeletionTime', {
    value: input.cacheExpiresAtMs ? new Date(input.cacheExpiresAtMs).toISOString() : '',
    valueKey: input.cacheExpiresAtMs ? undefined : 'fileInfo.value.unknown',
  });

  /* ---- saving ---- */

  add('fileInfo.lastSaved', {
    value: session?.lastSaved ? new Date(session.lastSaved.savedAtMs).toISOString() : '',
    valueKey: session?.lastSaved ? undefined : 'fileInfo.value.never',
  });
  add('fileInfo.saveResult', {
    valueKey: session?.lastFailure
      ? 'error.editsSavedTemporarily'
      : session?.lastSaved ? 'fileInfo.value.yes' : 'fileInfo.value.never',
    detail: session?.lastFailure ?? undefined,
  });
  add('fileInfo.validationResult', {
    valueKey: input.validation
      ? (input.validation.ok ? 'fileInfo.value.yes' : 'fileInfo.value.no')
      : 'fileInfo.value.unknown',
    detail: input.validation?.issues.join('; ') || undefined,
  });

  /* ---- unsupported features ---- */

  const unsupported = [
    ...(spec?.lossOnSave ?? []),
    ...(document?.lossOnSave ?? []),
  ];
  add('fileInfo.unsupportedSummary', {
    value: unsupported.length > 0 ? String(unsupported.length) : '',
    valueKey: unsupported.length > 0 ? undefined : 'fileInfo.value.no',
    detail: unsupported.join(' · ') || undefined,
  });

  return {
    rows,
    blocked: assessment.blockedFeatures.map((f) => ({ kind: f.kind, detail: f.detail })),
    warnings: [
      ...detection.warnings.map((w) => ({
        messageKey: w.messageKey,
        ...(w.detail === undefined ? {} : { detail: w.detail }),
      })),
      ...(document?.warnings ?? []).map((w) => ({
        messageKey: w.messageKey,
        ...(w.detail === undefined ? {} : { detail: w.detail }),
      })),
    ],
    lossOnSave: [...new Set(unsupported)],
    sensitive: assessment.sensitive,
  };
}
